import { addHours, format, startOfDay, startOfHour, subDays } from 'date-fns'
import { useMemo } from 'react'
import { useParams } from 'react-router'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/sv/chart'
import { H3, Muted } from '@/components/sv/typographies'
import { CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import { getXAxisDateFormat } from '@/config/charts'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import type {
  WebhookAnalyticsQuery,
  WebhookAnalyticsQueryVariables,
} from '@/routes/webhook/tabs/overview/sections/general/analytics/index.types.gen'

const WEBHOOK_ANALYTICS_QUERY = gql`
  query webhookAnalytics(
    $idCompany: String!
    $id: String!
    $start: String!
    $end: String!
    $intervalInSeconds: Int!
  ) {
    company(id: $idCompany) {
      id
      webhook(id: $id) {
        id
        failedDeliveries(
          start: $start
          end: $end
          intervalInSeconds: $intervalInSeconds
        ) {
          time
          value
        }
        successfulDeliveries(
          start: $start
          end: $end
          intervalInSeconds: $intervalInSeconds
        ) {
          time
          value
        }
        responseTime(
          start: $start
          end: $end
          intervalInSeconds: $intervalInSeconds
        ) {
          time
          value
        }
      }
    }
  }
`

const now = new Date()
const deliveryChartConfig = {
  successful: { label: 'Successful deliveries', color: 'var(--chart-2)' },
  failed: { label: 'Failed deliveries', color: 'var(--chart-1)' },
} satisfies ChartConfig
const responseTimeChartConfig = {
  avg: { label: 'Response time', color: 'var(--chart-3)' },
} satisfies ChartConfig

export const WebhookAnalyticsSection = () => {
  const { idWebhook = '' } = useParams()
  const { idCompany } = useIdCompany()

  const end = startOfHour(addHours(now, 1))
  const start = startOfDay(subDays(end, 7))
  const intervalInSeconds = 7200

  const { data } = useSuspenseQuery<
    WebhookAnalyticsQuery,
    WebhookAnalyticsQueryVariables
  >(WEBHOOK_ANALYTICS_QUERY, {
    variables: {
      idCompany,
      id: idWebhook,
      start: start.toISOString(),
      end: end.toISOString(),
      intervalInSeconds,
    },
  })

  const analytics = data?.company?.webhook

  const sumResponseTime = analytics?.responseTime?.reduce(
    (acc, delivery) => acc + Number(delivery.value),
    0
  )
  const responseTimeCount =
    analytics?.responseTime?.filter((e) => Boolean(e.value))?.length ?? 0
  const avgResponseTime = sumResponseTime
    ? Math.round(sumResponseTime / responseTimeCount)
    : 0

  const successfulDeliveriesPoints = useMemo(
    () =>
      analytics?.successfulDeliveries?.map((d: any) => ({
        time: Number(d?.time),
        value: Number(d?.value ?? '0'),
      })) ?? [],
    [analytics]
  )
  const failedDeliveriesPoints = useMemo(
    () =>
      analytics?.failedDeliveries?.map((d: any) => ({
        time: Number(d?.time),
        value: Number(d?.value ?? '0'),
      })) ?? [],
    [analytics]
  )
  const responseTimePoints = useMemo(
    () =>
      analytics?.responseTime?.map((d: any) => ({
        time: Number(d?.time),
        value: Number(d?.value ?? '0'),
      })) ?? [],
    [analytics]
  )

  type CombinedPoint = {
    successful: number
    failed: number
    time: number
  }
  const deliveryPoints: CombinedPoint[] = useMemo(() => {
    const byTime: Record<number, CombinedPoint> = {}

    const ensure = (t: number) =>
      (byTime[t] ??= { time: t, successful: 0, failed: 0 })

    successfulDeliveriesPoints.forEach(({ time, value }) => {
      ensure(time).successful = value ?? 0
    })
    failedDeliveriesPoints.forEach(({ time, value }) => {
      ensure(time).failed = value ?? 0
    })

    return Object.values(byTime).sort((a, b) => a.time - b.time)
  }, [successfulDeliveriesPoints, failedDeliveriesPoints])

  return (
    <div className="grid grid-cols-2">
      <div>
        <CardHeader className="py-6">
          <H3>Event deliveries</H3>
          <CardDescription>
            <div className="flex gap-6 mt-2">
              <div className="flex items-center gap-2">
                <div className="bg-[var(--chart-2)] w-4 h-4 rounded-sm" />
                <Muted>Succeeded</Muted>
                <Muted>
                  {(analytics?.successfulDeliveries ?? []).reduce(
                    (acc, delivery) => acc + Number(delivery.value),
                    0
                  )}
                </Muted>
              </div>
              <div className="flex items-center gap-2">
                <div className="bg-[var(--chart-1)] w-4 h-4 rounded-sm" />
                <Muted>Failed</Muted>
                <Muted>
                  {(analytics?.failedDeliveries ?? []).reduce(
                    (acc, delivery) => acc + Number(delivery.value),
                    0
                  )}
                </Muted>
              </div>
            </div>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ChartContainer config={deliveryChartConfig} className="h-60 w-full">
            <LineChart
              accessibilityLayer
              data={deliveryPoints}
              margin={{ left: 0, right: 0 }}
            >
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="time"
                scale="time"
                type="number"
                domain={[start.getTime(), end.getTime()]}
                tickLine={false}
                interval="equidistantPreserveStart"
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) =>
                  value &&
                  format(
                    value,
                    getXAxisDateFormat({
                      startDate: start,
                      endDate: end,
                      intervalInSeconds,
                    })
                  )
                }
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(val) => `${Math.round(Number(val ?? '0'))}`}
                allowDecimals={false}
              />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    indicator="dot"
                    hideLabel={false}
                    labelFormatter={(_label, items) => {
                      const t = items?.[0]?.payload?.time as number | undefined
                      return t ? format(t, 'MMM dd, yyyy HH:mm') : ''
                    }}
                    valueFormatter={(v) => `${Math.round(Number(v ?? 0))}`}
                  />
                }
              />
              <Line
                dataKey="successful"
                type="step"
                fillOpacity={0.1}
                strokeWidth={1}
                fill={deliveryChartConfig.successful.color}
                stroke={deliveryChartConfig.successful.color}
                name={deliveryChartConfig.successful.label}
                connectNulls={false}
                key={'successful'}
                dot={false}
              />
              <Line
                dataKey="failed"
                type="step"
                fillOpacity={0}
                strokeWidth={1}
                stroke={deliveryChartConfig.failed.color}
                name={deliveryChartConfig.failed.label}
                connectNulls={false}
                key={'failed'}
                dot={false}
              />
            </LineChart>
          </ChartContainer>
        </CardContent>
      </div>
      <div>
        <CardHeader className="py-6">
          <H3>Response time</H3>
          <CardDescription>
            <div className="flex items-center gap-2 mt-2">
              <div className="bg-[var(--chart-3)] w-4 h-4 rounded-sm" />
              <Muted>Average</Muted>
              <Muted>{avgResponseTime} ms</Muted>
            </div>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ChartContainer
            config={responseTimeChartConfig}
            className="h-60 w-full"
          >
            <LineChart
              accessibilityLayer
              data={responseTimePoints}
              margin={{ left: 0, right: 0 }}
            >
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="time"
                scale="time"
                type="number"
                domain={[start.getTime(), end.getTime()]}
                tickLine={false}
                interval="equidistantPreserveStart"
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) =>
                  value &&
                  format(
                    value,
                    getXAxisDateFormat({
                      startDate: start,
                      endDate: end,
                      intervalInSeconds,
                    })
                  )
                }
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(val) => `${Math.round(Number(val ?? '0'))} ms`}
                allowDecimals={false}
              />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    indicator="dot"
                    hideLabel={false}
                    labelFormatter={(_label, items) => {
                      const t = items?.[0]?.payload?.time as number | undefined
                      return t ? format(t, 'MMM dd, yyyy HH:mm') : ''
                    }}
                    valueFormatter={(v) => `${Math.round(Number(v ?? 0))} ms`}
                  />
                }
              />
              <Line
                dataKey="value"
                type="step"
                fillOpacity={0.1}
                strokeWidth={1}
                fill={responseTimeChartConfig.avg.color}
                stroke={responseTimeChartConfig.avg.color}
                name={responseTimeChartConfig.avg.label}
                connectNulls={false}
                key={'value'}
                dot={false}
              />
            </LineChart>
          </ChartContainer>
        </CardContent>
      </div>
    </div>
  )
}
